import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import VenueForm from '../components/VenueForm';
import {
  addDoc,
  collection,
  serverTimestamp,
  updateDoc,
  doc
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase/config';
import { logger } from "../utils/logger";
import { DEFAULT_CITY } from '../config/cities';

const AddVenuePage = () => {
  const navigate = useNavigate();
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);

  // Parse state from URL (supports both URL-encoded and legacy base64 formats)
  const stateData = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const stateParam = params.get('state');

      if (stateParam) {
        // Try URL-encoded JSON first (new format, handles Unicode properly)
        try {
          const decoded = JSON.parse(decodeURIComponent(stateParam));
          logger.info('Decoded state (URL-encoded):', decoded);
          return decoded;
        } catch (urlDecodeError) {
          // Fallback to base64 for backwards compatibility
          const decoded = JSON.parse(atob(stateParam));
          logger.info('Decoded state (base64 legacy):', decoded);
          return decoded;
        }
      }
      return {};
    } catch (error) {
      logger.error('Error parsing state:', error);
      return {};
    }
  })();

  // Extract fields from state data
  const {
    suggestionId,
    initialSearchQuery,
    // Pre-filled venue data from suggestion
    headline: prefillHeadline,
    address: prefillAddress,
    phone: prefillPhone,
    link: prefillLink,
    place_id: prefillPlaceId,
    lat: prefillLat,
    lon: prefillLon,
    city: prefillCity,
    main_offer: prefillMainOffer,
    offer_details: prefillOfferDetails,
    // Legacy field for backwards compatibility
    dealDescription
  } = stateData;

  const [formData, setFormData] = useState({
    headline: prefillHeadline || '',
    address: prefillAddress || '',
    phone: prefillPhone || '',
    link: prefillLink || '',
    category: 'hh',
    city: prefillCity || DEFAULT_CITY,
    lat: prefillLat || '',
    lon: prefillLon || '',
    is_active: true,
    main_offer: prefillMainOffer || dealDescription || '', // Support both new and legacy formats
    offer_details: prefillOfferDetails || '',
    place_id: prefillPlaceId || '',
    opening_hours: {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: []
    }
  });

  const handleSubmit = async (venueData) => {
    try {
      setLoading(true);

      const venueToSave = {
        ...venueData,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        category: 'hh',
        is_active: true
      };

      // Remove temp_image_url before saving (it's just for preview)
      delete venueToSave.temp_image_url;

      const docRef = await addDoc(collection(db, 'venues'), venueToSave);
      logger.log('Venue created with ID:', docRef.id);

      // Download and store image server-side if venue has a place_id
      if (venueData.place_id) {
        try {
          logger.log('Calling downloadVenueImage for venue:', docRef.id);
          const downloadVenueImage = httpsCallable(functions, 'downloadVenueImage');
          const result = await downloadVenueImage({
            venueId: docRef.id,
            placeId: venueData.place_id
          });
          logger.log('Image download result:', result.data);
        } catch (imageError) {
          // Log but don't fail - venue is saved, image can be added later
          logger.warn('Image download failed (venue still saved):', imageError);
        }
      }

      if (suggestionId) {
        await updateDoc(doc(db, 'deal_suggestions', suggestionId), {
          status: 'complete'
        });
      }

      setAlert({
        open: true,
        message: 'Venue added successfully!',
        severity: 'success'
      });

      setTimeout(() => navigate('/admin'), 2000);
    } catch (error) {
      logger.error('Error in handleSubmit:', error);
      setAlert({
        open: true,
        message: 'Failed to add venue. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin');
  };

  const handleGooglePlaceSelected = (placeData) => {
    logger.log('Google place data received:', placeData);
    setFormData(prevData => ({
      ...prevData,
      ...placeData,
      category: 'hh', // Ensure category remains set
      is_active: true, // Ensure is_active remains set
    }));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {loading && (
        <Box sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          backgroundColor: 'rgba(255, 255, 255, 0.7)', 
          zIndex: 9999 
        }}>
          <CircularProgress />
        </Box>
      )}

      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Add New Venue
        </Typography>

        {prefillPlaceId && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <strong>Pre-filled from suggestion:</strong> This form has been populated with Google Maps data.
            Review the details and fill in the happy hour times, then save.
          </Alert>
        )}

        <VenueForm
          mode="add"
          venue={formData}
          initialSearchQuery={initialSearchQuery} // Pass the search query directly
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          onGooglePlaceSelected={handleGooglePlaceSelected}
        />
      </Paper>

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddVenuePage;